/**
 * @copyright (C) 2023-2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-21 16:36:46
 **/

import { BaseServiceV2 } from "@/frameworks/libraries/restservice";

class Chat extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (roomId?: string, path?: string, threadMessageId?: string) =>
		`/api/chat${roomId ? `/${roomId}` : ""}${path ? `/${path}` : ""}${threadMessageId ? `/${threadMessageId}` : ""}`;

	private APIPath1 = (roomMessageId?: string, path?: string) =>
		`/api/chat/messages${roomMessageId ? `/${roomMessageId}` : ""}${path ? `/${path}` : ""}`;

	public deleteMessages = {
		name: "deleteMessage",
		execute: ({
			roomMessageId,
			roomId
		}: WebServices.Chat.Delete.Request): Promise<WebServices.Chat.Delete.Response> =>
			this.delete(this.deleteMessages.name, this.APIPath1(roomMessageId), {
				roomId
			})
	};

	public messages = {
		name: "messages",
		execute: ({
			roomId,
			...parameter
		}: WebServices.Chat.Messages.Request): Promise<WebServices.Chat.Messages.Response> =>
			this.get(this.messages.name, this.APIPath(roomId, "messages"), parameter)
	};

	public searchMessages = {
		name: "searchMessages",
		execute: (parameter: WebServices.Chat.Search.Request): Promise<WebServices.Chat.Search.Response> =>
			this.get(this.searchMessages.name, this.APIPath1(), parameter)
	};

	public sendMessage = {
		name: "sendMessage",
		execute: ({
			roomId,
			...parameter
		}: WebServices.Chat.SendMessage.Request): Promise<WebServices.Chat.SendMessage.Response> =>
			this.post(this.sendMessage.name, this.APIPath(roomId, "messages"), parameter)
	};

	public updateMessage = {
		name: "updateMessage",
		execute: ({
			roomMessageId,
			...parameter
		}: WebServices.Chat.Update.Request): Promise<WebServices.Chat.Update.Response> =>
			this.put(this.updateMessage.name, this.APIPath1(roomMessageId), parameter)
	};

	public getRoom = {
		name: "getRoom",
		execute: ({ roomID }: WebServices.Chat.GetRoom.Request): Promise<WebServices.Chat.GetRoom.Response> =>
			this.get(this.getRoom.name, this.APIPath(roomID))
	};

	public pinRoom = {
		name: "pinRoom",
		execute: ({ roomId, pin }: WebServices.Chat.Pin.Request): Promise<WebServices.Chat.Pin.Response> =>
			this.put(this.pinRoom.name, this.APIPath(roomId, "pin"), { pin })
	};

	public reaction = {
		add: {
			name: "reaction:add",
			execute: (
				parameter: WebServices.Chat.Reaction.Add.Request
			): Promise<WebServices.Chat.Reaction.Add.Response> =>
				this.post(this.reaction.add.name, this.APIPath("reactions"), parameter)
		},
		remove: {
			name: "reaction:remove",
			execute: (
				parameter: WebServices.Chat.Reaction.Remove.Request
			): Promise<WebServices.Chat.Reaction.Remove.Response> =>
				this.delete(this.reaction.remove.name, this.APIPath("reactions"), parameter)
		}
	};

	public searchUser = {
		name: "searchUser",
		execute: (
			parameter: WebServices.Chat.SearchUsers.Request = {}
		): Promise<WebServices.Chat.SearchUsers.Response> =>
			this.get(this.searchUser.name, this.APIPath("users"), parameter)
	};

	public messageReaded = {
		name: "messageReaded",
		execute: (
			parameter: WebServices.Chat.MessageReaded.Request
		): Promise<WebServices.Chat.MessageReaded.Response> =>
			this.post(this.messageReaded.name, this.APIPath("message_read"), parameter)
	};

	public messageReadAll = {
		name: "messageReadAll",
		execute: (
			parameter: WebServices.Chat.MessageReadAll.Request
		): Promise<WebServices.Chat.MessageReadAll.Response> =>
			this.post(this.messageReadAll.name, this.APIPath("message_read_all"), parameter)
	};

	public roomView = {
		name: "roomView",
		execute: (parameter?: WebServices.Chat.RoomView.Request): Promise<WebServices.Chat.RoomView.Response> =>
			this.get(this.roomView.name, this.APIPath("room_view"), parameter)
	};

	public updateRoomView = {
		name: "updateRoomView",
		execute: ({
			...parameter
		}: WebServices.Chat.UpdateRoomView.Request): Promise<WebServices.Chat.UpdateRoomView.Response> =>
			this.post(this.updateRoomView.name, this.APIPath("room_view"), parameter)
	};

	public updateNotification = {
		name: "update:notification",
		execute: (
			parameter: WebServices.Chat.UpdateNotification.Request
		): Promise<WebServices.Chat.UpdateNotification.Response> =>
			this.post(this.updateNotification.name, this.APIPath("notification"), parameter)
	};

	public deleteFile = {
		name: "delete:chatFile",
		execute: (parameter: WebServices.Chat.DeleteFile.Request): Promise<WebServices.Chat.DeleteFile.Response> =>
			this.delete(this.deleteFile.name, this.APIPath1(parameter.roomMessageId, parameter.fileBoxId), parameter)
	};

	public changeOwner = {
		name: "changeOwner",
		execute: (parameter: WebServices.Chat.ChangeOwner.Request): Promise<WebServices.Chat.ChangeOwner.Response> =>
			this.put(this.deleteFile.name, this.APIPath("", "change_owner"), parameter)
	};

	public getThread = {
		name: "getThread",
		execute: ({
			roomID,
			...parameter
		}: WebServices.Chat.GetRoomThread.Request): Promise<WebServices.Chat.GetRoomThread.Response> =>
			this.get(this.getThread.name, this.APIPath(roomID, "thread_messages"), parameter)
	};

	public readThread = {
		name: "readThread",
		execute: (
			parameter: WebServices.Chat.GetRoomThread.Request
		): Promise<WebServices.Chat.GetRoomThread.Response> =>
			this.post(this.getThread.name, this.APIPath("thread_message_read"), parameter)
	};

	public bookmark = {
		add: {
			name: "bookmark:add",
			execute: (
				parameter: WebServices.Chat.Bookmark.Add.Request
			): Promise<WebServices.Chat.Bookmark.Add.Response> =>
				this.post(this.reaction.add.name, this.APIPath("bookmarks"), parameter)
		},
		remove: {
			name: "bookmark:remove",
			execute: (
				parameter: WebServices.Chat.Bookmark.Remove.Request
			): Promise<WebServices.Chat.Bookmark.Remove.Response> =>
				this.delete(this.reaction.remove.name, this.APIPath("bookmarks"), parameter)
		}
	};
}

class ChatVote extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (voteId?: string) => `/api/vote${voteId ? `/${voteId}` : ""}`;

	public deleteVote = {
		name: "vote:delete",
		execute: (parameter: WebServices.ChatVote.Delete.Request): Promise<WebServices.ChatVote.Delete.Response> =>
			this.delete(this.deleteVote.name, this.APIPath(parameter.voteId), parameter)
	};

	public detail = {
		name: "vote:detail",
		execute: (parameter: WebServices.ChatVote.Detail.Request): Promise<WebServices.ChatVote.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(parameter.voteId), parameter)
	};

	public create = {
		name: "vote:create",
		execute: (parameter: WebServices.ChatVote.Create.Request): Promise<WebServices.ChatVote.Create.Response> =>
			this.post(this.deleteVote.name, this.APIPath(), parameter)
	};

	public end = {
		name: "vote:end",
		execute: (parameter: WebServices.ChatVote.End.Request): Promise<WebServices.ChatVote.End.Response> =>
			this.post(this.end.name, this.APIPath("end"), parameter)
	};

	public choose = {
		name: "vote:choose",
		execute: (parameter: WebServices.ChatVote.Choose.Request): Promise<WebServices.ChatVote.Choose.Response> =>
			this.post(this.choose.name, this.APIPath("choose"), parameter)
	};

	public update = {
		name: "vote:update",
		execute: (parameter: WebServices.ChatVote.Update.Request): Promise<WebServices.ChatVote.Update.Response> =>
			this.put(this.update.name, this.APIPath(parameter.voteId), parameter)
	};
}

class ChatDM extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (roomId?: string, path?: string) =>
		`/api/dm${roomId ? `/${roomId}` : ""}${path ? `/${path}` : ""}`;

	public list = {
		name: "dm:list",
		execute: (
			parameter: WebServices.DirectMessage.List.Request = {}
		): Promise<WebServices.DirectMessage.List.Response> => this.get(this.list.name, this.APIPath(), parameter)
	};

	public create = {
		name: "dm:create",
		execute: (
			parameter: WebServices.DirectMessage.Create.Request
		): Promise<WebServices.DirectMessage.Create.Response> => this.post(this.create.name, this.APIPath(), parameter)
	};

	public addUsers = {
		name: "dm:addUsers",
		execute: ({
			roomId,
			...parameter
		}: WebServices.DirectMessage.AddUsers.Request): Promise<WebServices.DirectMessage.AddUsers.Response> =>
			this.post(this.addUsers.name, this.APIPath(roomId, "users"), parameter)
	};

	public hide = {
		name: "dm:hide",
		execute: ({
			roomId
		}: WebServices.DirectMessage.Hide.Request): Promise<WebServices.DirectMessage.Hide.Response> =>
			this.put(this.hide.name, this.APIPath(roomId, "hide"))
	};

	public checkExist = {
		name: "dm:checkExist",
		execute: ({
			userIds
		}: WebServices.DirectMessage.CheckDMExists.Request["requestVO"]): Promise<WebServices.DirectMessage.CheckDMExists.Response> =>
			this.get(
				this.checkExist.name,
				this.APIPath("dm_check"),
				{ userIds },
				{
					paramsSerializer: (paramObj) => {
						const params = new URLSearchParams();
						for (const key in paramObj) {
							params.append(key, paramObj[key]);
						}

						return params.toString();
					}
				}
			)
	};

	public checkAuth = {
		name: "dm:checkAuth",
		execute: ({
			userId
		}: WebServices.DirectMessage.CheckAuth.Request): Promise<WebServices.DirectMessage.CheckAuth.Response> =>
			this.get(this.checkAuth.name, this.APIPath("check_one_to_one_allowed"), { userId })
	};
}

class ChatSpace extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (roomId?: string, path?: string) =>
		`/api/space_chat${roomId ? `/${roomId}` : ""}${path ? `/${path}` : ""}`;

	public list = {
		name: "space:list",
		execute: (parameter: WebServices.SpaceChat.List.Request = {}): Promise<WebServices.SpaceChat.List.Response> =>
			this.get(this.list.name, this.APIPath(), parameter)
	};

	public groupedList = {
		name: "spaceChat:groupedList",
		execute: (
			parameter: WebServices.SpaceChat.GroupedList.Request = {}
		): Promise<WebServices.SpaceChat.GroupedList.Response> =>
			this.get(this.groupedList.name, this.APIPath("grouped_space_chat"), parameter)
	};

	public create = {
		name: "space:create",
		execute: (parameter: WebServices.SpaceChat.Create.Request): Promise<WebServices.SpaceChat.Create.Response> =>
			this.post(this.create.name, this.APIPath(), parameter)
	};

	public addUsers = {
		name: "space:addUsers",
		execute: ({
			roomId,
			...rest
		}: WebServices.SpaceChat.AddSpaceChatUser.Request): Promise<WebServices.SpaceChat.AddSpaceChatUser.Response> =>
			this.post(this.addUsers.name, this.APIPath(`${roomId}/users`), rest)
	};

	public inquiry = {
		name: "spaceChat:inquiry",
		execute: ({
			spaceId
		}: WebServices.SpaceChat.Inquiry.Request): Promise<WebServices.SpaceChat.Inquiry.Response> =>
			this.get(this.inquiry.name, this.APIPath(spaceId))
	};

	public join = {
		name: "spaceChat:join",
		execute: (parameter: WebServices.SpaceChat.Join.Request): Promise<WebServices.SpaceChat.Join.Response> =>
			this.post(this.join.name, this.APIPath("join"), parameter)
	};

	public leave = {
		name: "spaceChat:leave",
		execute: (parameter: WebServices.SpaceChat.Leave.Request): Promise<WebServices.SpaceChat.Leave.Response> =>
			this.post(this.leave.name, this.APIPath("leave"), parameter)
	};

	public remove = {
		name: "spaceChat:delete",
		execute: ({
			roomId,
			...parameter
		}: WebServices.SpaceChat.Delete.Request): Promise<WebServices.SpaceChat.Delete.Response> =>
			this.delete(this.remove.name, this.APIPath(roomId), parameter)
	};

	public topBarColor = {
		name: "spaceChat:topbarcolor",
		execute: (
			parameter: WebServices.SpaceChat.TopBarColor.Request
		): Promise<WebServices.SpaceChat.TopBarColor.Response> =>
			this.post(this.topBarColor.name, this.APIPath("top_bar_color"), parameter)
	};

	public update = {
		name: "spaceChat:update",
		execute: (parameter: WebServices.SpaceChat.Update.Request): Promise<WebServices.SpaceChat.Update.Response> =>
			this.put(this.update.name, this.APIPath(""), parameter)
	};
}

class ChatNotify extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/chat/notice${path ? `/${path}` : ""}`;

	public register = {
		name: "chatNotice:register",
		execute: (parameter: WebServices.Chat.Notify.Request): Promise<WebServices.Chat.Notify.Response> =>
			this.post(this.register.name, this.APIPath(), parameter)
	};

	public remove = {
		name: "chatNotice:remove",
		execute: (parameter: WebServices.Chat.DeleteNotice.Request): Promise<WebServices.Chat.DeleteNotice.Response> =>
			this.post(this.remove.name, this.APIPath("delete"), parameter)
	};
}

class ChatAlarmNotification extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/chat/notification${path ? `/${path}` : ""}`;

	public inquery = {
		name: "chatAlarmNotification:inquery",
		execute: (
			parameter: WebServices.Chat.GetChatNotifications.Request
		): Promise<WebServices.Chat.GetChatNotifications.Response> =>
			this.get(this.inquery.name, this.APIPath("unread_first"), parameter)
	};
}

class ChatActivity extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (path?: string) => `/api/chat/activity${path ? `/${path}` : ""}`;

	public getActivities = {
		name: "chatActivity:getActivities",
		execute: (
			parameter: WebServices.ChatActivity.GetActivities.Request
		): Promise<WebServices.ChatActivity.GetActivities.Response> =>
			this.get(this.getActivities.name, this.APIPath(), parameter)
	};

	public getActivity = {
		name: "chatActivity:getActivity",
		execute: ({
			roomMessageId,
			...parameter
		}: WebServices.ChatActivity.GetActivity.Request): Promise<WebServices.ChatActivity.GetActivity.Response> =>
			this.get(this.getActivities.name, this.APIPath(roomMessageId), parameter)
	};
}

export default class ChatService {
	constructor(
		events: API.Events,
		public readonly chat = new Chat(),
		public readonly chatDM = new ChatDM(),
		public readonly chatSpace = new ChatSpace(),
		public readonly chatNotify = new ChatNotify(),
		public readonly chatVote = new ChatVote(),
		public readonly chatAlarmNotification = new ChatAlarmNotification(),
		public readonly chatActivity = new ChatActivity()
	) {
		chat.set_event(events);
		chatDM.set_event(events);
		chatSpace.set_event(events);
		chatNotify.set_event(events);
		chatVote.set_event(events);
		chatAlarmNotification.set_event(events);
		chatActivity.set_event(events);
	}
}
